import styles from 'css/src/components/app/chat-tab/components/ChatInput.module.scss';

import React from 'react';
import { useSelector } from 'react-redux';
import classNames from 'classnames';

import { useChatHistory } from '../hooks/useChatHistory';

import AgentModal from './AgentModal';
import ModelSettingsPopover from './ModelSettingsPopover';
import RegistriesModal from './RegistriesModal';
import SkillsModal from './SkillsModal';
import SystemPromptModal from './SystemPromptModal';
import ToolsModal from './ToolsModal';

import type { ToolDefinition } from 'js/src/agent';
import type { SkillSurface } from 'js/src/agent/skills/types';
import type { ToolChildConversationSettings } from 'js/src/agent/toolChildConversationSettings';
import { conversationDetailUrl } from 'js/src/agent/subagentConversations';
import type { ModelOption, ReasoningEffort } from 'js/src/agent/types';
import { useRateLimitStatus } from 'js/src/agent/useRateLimitStatus';
import type { UsagePart } from 'js/src/agent/usageAttribution';
import { CostSummary } from 'js/src/components/codeo/CostSummary';
import { Toast } from 'js/src/components/common/Toast';
import {
    BUTTON_COLORS,
    BUTTON_VARIANTS,
    PrimaryIconButton,
} from 'js/src/components/common/ui/Button';
import type { MentionChatEditorRef } from 'js/src/components/common/mentions/MentionChatEditor';
import MentionChatEditor from 'js/src/components/common/mentions/MentionChatEditor';
import type { Reference } from 'js/src/components/common/mentions/ReferenceMentionNode';
import type { TypeInputChangeEvent } from 'js/src/components/common/ui/TextInput';
import TextInput, { TEXT_INPUT_VARIANTS } from 'js/src/components/common/ui/TextInput';
import Tooltip from 'js/src/components/common/ui/Tooltip';
import UploadedMediaPickerModal from 'js/src/components/media-library/UploadedMediaPickerModal';
import { ChatDropdown } from 'js/src/components/sketch/ChatDropdown';
import type {
    ChatUploadDropdownItem,
    ChatUploadDropdownRef,
} from 'js/src/components/sketch/ChatUploadDropdown';
import { ChatFileList } from 'js/src/components/sketch/ChatFileList';
import type { UploadedFile } from 'js/src/components/sketch/FileUpload';
import { useFileUpload } from 'js/src/components/sketch/FileUpload';
import { SketchDropZone } from 'js/src/components/sketch/SketchDropZone';
import { getCanUseAIVoiceover } from 'js/src/data/account';
import { SCRIPT_COMPOSER_MEDIA_FOLDER_NAME } from 'js/src/data/media';
import Flags from 'js/src/libs/flags';

interface ChatInputProps {
    onSendMessage: (messageText: string, attachedFiles?: UploadedFile[]) => void;
    isProcessing?: boolean;
    /** Called when the user clicks the stop button while processing. When provided,
     * the send button becomes a stop button during processing. */
    onStop?: () => void;
    /** Usage split per model, so subagent tokens are priced at the subagent's rate. */
    costParts?: UsagePart[];
    selectedModel?: ModelOption;
    /** All models selectable from the dev footer's model picker. */
    modelOptions?: ModelOption[];
    /** Called with the chosen model value from the dev footer's model picker. */
    onSelectModel?: (value: string) => void;
    /** Currently-selected reasoning effort, shown/picked in the dev footer. */
    reasoningEffort?: ReasoningEffort;
    /** Called with the chosen reasoning effort from the dev footer's picker. */
    onSelectReasoningEffort?: (value: ReasoningEffort) => void;
    /** Tool definitions to surface in the developer "see tools" modal. */
    toolDefinitions?: ToolDefinition[] | null;
    /** Maps a tool's function name to its Material Symbols icon, shown beside
     * each tool in the "see tools" modal. */
    iconForTool?: (name: string) => string | undefined;
    /** Maps a tool's function name to the model/reasoning effort its child
     * conversation runs at, editable per tool in the "see tools" modal. */
    childConversationSettingsForTool?: (name: string) => ToolChildConversationSettings | undefined;
    /** Currently-selected root agent value. When set together with
     * onSelectAgent, a link (labelled with the agent's name) opens the developer
     * Agent picker modal. */
    agentValue?: string;
    /** Display name of the selected agent, shown as the picker link text. */
    agentLabel?: string;
    /** Called with the chosen agent value from the Agent modal. */
    onSelectAgent?: (agentValue: string) => void;
    /** When set, a "skills" link is shown next to "see tools" that opens the
     * developer Skills modal (view/edit/enable/add/remove) for this surface. */
    skillSurface?: SkillSurface;
    /** Fired after a skill mutation in the Skills modal, so the parent can
     * re-read its active skill set. Required for the skills link to appear. */
    onSkillsChange?: () => void;
    /** The system prompt currently in effect. When set together with
     * onSystemPromptChange, a "sys" link opens the developer System prompt
     * modal (view/edit). */
    systemPrompt?: string | null;
    /** Called with the edited prompt when the user saves in the System prompt
     * modal. Required for the sys link to appear. */
    onSystemPromptChange?: (prompt: string) => void;
    /** True when the prompt has been hand-edited away from the agent default. */
    isSystemPromptOverridden?: boolean;
    /** Restores the agent's default prompt from the System prompt modal. */
    onSystemPromptReset?: () => void;
    conversationId?: string;
    /** Developer-tools override for the Responses API compaction threshold (tokens);
     * undefined means the model default is in effect. */
    compactionThreshold?: number;
    /** The threshold used when no override is set, shown as the field's placeholder. */
    defaultCompactionThreshold?: number;
    /** Called with the new override (or undefined to clear it) when saved in the modal. */
    onCompactionThresholdChange?: (threshold: number | undefined) => void;
    placeholder?: string;
    header?: React.ReactNode;
    footer?: React.ReactNode;
    showSketchControls?: boolean;
    hideBlueprint?: boolean;
    canAttachFiles?: boolean;
    canAttachExistingMedia?: boolean;
    hideControls?: boolean;
    disabledTooltip?: string;
    className?: string;
    /** Called before sending - return false to prevent the message from being sent */
    onBeforeSubmit?: (messageText: string, attachedFiles: UploadedFile[]) => boolean;
    /** If true, don't clear input after submit (for custom navigation flows) */
    keepInputAfterSubmit?: boolean;
    autoFocus?: boolean;
    onFocus?: () => void;
    onBlur?: () => void;
    noPadding?: boolean;
    /** When set, the textarea is replaced by an editor where typing "@" offers
     * these references as inline mentions. */
    atMentionReferences?: Reference[];
    /** Rendered in the bottom control row, after the settings button. */
    recipeSlot?: React.ReactNode;
    /** Extra items appended to the "+" attach menu. */
    attachMenuItems?: ChatUploadDropdownItem[];
    ref?: React.Ref<ChatInputRef>;
}

/**
 * Lets a caller drive the parts of the input it does not own, for flows that
 * start outside it and land here — a "Start from" pill that prefills the chat
 * and then needs the caret in it, or one whose first step is attaching a file.
 */
export interface ChatInputRef {
    focusInput: () => void;
    openAttachMenu: () => void;
    /**
     * Prefills the draft and puts the caret at the end of it. Text the user has
     * typed is theirs to keep, so a seed lands after it rather than over it;
     * only an untouched draft (empty, or seeded by an earlier caller) is
     * replaced outright.
     */
    seedText: (text: string) => void;
    /**
     * Attaches an already-uploaded asset by uuid, as if it had been picked from
     * the user's uploads. For flows that produce an asset elsewhere — recording
     * a talking head or a voiceover — and need it to land here as a chip.
     * Resolves to whether the asset was actually attached; it reports its own
     * failure, so a caller only needs this to decide its own follow-up.
     */
    attachExistingMedia: (assetUuid: string, isVoiceover: boolean) => Promise<boolean>;
}

/**
 * Input area component for the chat interface including text input, send button, file uploads, and token counter.
 * Handles user input, form submission, file attachments, and displays token usage information.
 *
 * @param onSendMessage Callback to send the current message with optional file attachments
 * @param isProcessing Whether a message is currently being processed (disables input)
 * @param costParts Per-model token usage, priced and totalled in the footer
 * @param selectedModel Currently selected model (for the footer's model picker)
 */
export default function ChatInput({
    onSendMessage,
    isProcessing = false,
    onStop,
    costParts,
    selectedModel,
    modelOptions,
    onSelectModel,
    reasoningEffort,
    onSelectReasoningEffort,
    toolDefinitions,
    iconForTool,
    childConversationSettingsForTool,
    agentValue,
    agentLabel,
    onSelectAgent,
    skillSurface,
    onSkillsChange,
    systemPrompt,
    onSystemPromptChange,
    isSystemPromptOverridden,
    onSystemPromptReset,
    conversationId,
    compactionThreshold,
    defaultCompactionThreshold,
    onCompactionThresholdChange,
    placeholder = 'What would you like help with?',
    header,
    footer,
    showSketchControls = false,
    hideBlueprint = false,
    canAttachFiles = true,
    canAttachExistingMedia = false,
    hideControls = false,
    disabledTooltip,
    className,
    onBeforeSubmit,
    keepInputAfterSubmit = false,
    autoFocus = true,
    onFocus,
    onBlur,
    noPadding = false,
    atMentionReferences,
    recipeSlot,
    attachMenuItems,
    ref,
}: ChatInputProps) {
    const [inputText, setInputText] = React.useState('');
    const [uploadedFiles, setUploadedFiles] = React.useState<UploadedFile[]>([]);
    const [showToolsModal, setShowToolsModal] = React.useState(false);
    const [showSkillsModal, setShowSkillsModal] = React.useState(false);
    const [showAgentModal, setShowAgentModal] = React.useState(false);
    const [showRegistriesModal, setShowRegistriesModal] = React.useState(false);
    const [showSystemPromptModal, setShowSystemPromptModal] = React.useState(false);
    const [showExistingMediaModal, setShowExistingMediaModal] = React.useState(false);
    const { isRateLimited, message: rateLimitMessage } = useRateLimitStatus();
    const canUseAIVoiceover = useSelector(getCanUseAIVoiceover);
    const chatTextareaRef = React.useRef<HTMLInputElement | HTMLTextAreaElement>(null);
    const mentionEditorRef = React.useRef<MentionChatEditorRef>(null);
    const attachMenuRef = React.useRef<ChatUploadDropdownRef>(null);

    // Whether what's in the draft came from the user rather than from a seed, so
    // `seedText` knows if it has anything worth preserving.
    const isDraftUserWrittenRef = React.useRef(false);

    const setUserWrittenText = React.useCallback((text: string) => {
        isDraftUserWrittenRef.current = text.trim().length > 0;
        setInputText(text);
    }, []);

    const handleInputChange = React.useCallback(
        (e: TypeInputChangeEvent) => {
            setUserWrittenText(e.target.value);
        },
        [setUserWrittenText],
    );

    // Only one of the two editors is mounted at a time, so focus whichever is.
    const focusInput = React.useCallback(() => {
        (mentionEditorRef.current ?? chatTextareaRef.current)?.focus();
    }, []);

    const seedText = React.useCallback((text: string) => {
        setInputText((draft) =>
            isDraftUserWrittenRef.current ? `${draft.trimEnd()}\n\n${text}` : text,
        );
        isDraftUserWrittenRef.current = false;
        // Put the caret at the end so the user can keep typing where the seeded
        // text left off, once the textarea has rendered the new value.
        requestAnimationFrame(() => {
            const textarea = chatTextareaRef.current;
            if (!textarea) {
                // The mention editor is rendering instead; focusing it already
                // leaves the caret at the end of the seeded text.
                mentionEditorRef.current?.focus();
                return;
            }
            textarea.focus();
            const end = textarea.value.length;
            textarea.setSelectionRange?.(end, end);
        });
    }, []);

    const { recallLast, saveToHistory } = useChatHistory({
        onHistoryChange: (entry) => {
            handleInputChange({ target: { value: entry.text } } as TypeInputChangeEvent);
            if (entry.files.length > 0) {
                setUploadedFiles(entry.files);
            }
        },
    });

    // The editor only reports ArrowUp on an empty draft; a queued attachment
    // still counts as a draft in progress, matching the textarea's guard.
    const handleHistoryRecall = React.useCallback(() => {
        if (uploadedFiles.length === 0) {
            recallLast();
        }
    }, [recallLast, uploadedFiles]);

    const { handleFilesSelected, addExistingMedia } = useFileUpload({
        onFilesUploaded: setUploadedFiles,
        currentFiles: uploadedFiles,
        folderName: SCRIPT_COMPOSER_MEDIA_FOLDER_NAME,
    });

    // Declared after `useFileUpload` so the handle can expose its
    // `addExistingMedia` — a flow that starts outside the input (recording a
    // video) still has to land its asset here as an attachment.
    React.useImperativeHandle(ref, () => ({
        focusInput,
        openAttachMenu: () => attachMenuRef.current?.openMenu(),
        seedText,
        attachExistingMedia: addExistingMedia,
    }));

    const openExistingMediaPicker = React.useCallback(() => {
        setShowExistingMediaModal(true);
    }, []);

    const closeExistingMediaPicker = React.useCallback(() => {
        setShowExistingMediaModal(false);
    }, []);

    const handleExistingMediaSelected = React.useCallback(
        (mediaId: string, isVoiceover: boolean) => {
            void addExistingMedia(mediaId, isVoiceover);
            closeExistingMediaPicker();
            // Return focus to the chat input after picking media; only one of
            // the two refs is attached at a time.
            requestAnimationFrame(() =>
                (mentionEditorRef.current ?? chatTextareaRef.current)?.focus(),
            );
        },
        [addExistingMedia, closeExistingMediaPicker],
    );

    const handleChooseExisting = canAttachExistingMedia ? openExistingMediaPicker : undefined;

    const handleRemoveFile = React.useCallback((fileId: string) => {
        setUploadedFiles((prev) => prev.filter((file) => file.id !== fileId));
    }, []);

    const hasUploadingFiles = uploadedFiles.some((file) => file.isUploading);
    const hasValidFiles =
        uploadedFiles.filter((file) => !file.isUploading && !file.error).length > 0;

    // Combine explicit disabled prop with rate limiting state
    const effectiveDisabled = hideControls || isRateLimited;
    const effectiveTooltip = isRateLimited ? (rateLimitMessage ?? undefined) : disabledTooltip;

    const canSubmit =
        (inputText.trim() || hasValidFiles) &&
        !isProcessing &&
        !hasUploadingFiles &&
        !effectiveDisabled;

    const handleSubmit = React.useCallback(
        (e?: React.FormEvent) => {
            e?.preventDefault();
            // Show toast if rate limited and user tries to submit
            if (isRateLimited && rateLimitMessage) {
                Toast.error(rateLimitMessage);
                return;
            }
            if (canSubmit) {
                const filesToSend = uploadedFiles.filter(
                    (file) => !file.isUploading && !file.error,
                );

                // Allow parent to intercept and cancel submission
                if (onBeforeSubmit && !onBeforeSubmit(inputText.trim(), filesToSend)) {
                    return;
                }

                saveToHistory(inputText.trim(), filesToSend);
                onSendMessage(inputText, filesToSend.length > 0 ? filesToSend : undefined);

                if (!keepInputAfterSubmit) {
                    setUserWrittenText('');
                    setUploadedFiles([]);
                }
            }
        },
        [
            canSubmit,
            uploadedFiles,
            onSendMessage,
            inputText,
            saveToHistory,
            onBeforeSubmit,
            keepInputAfterSubmit,
            isRateLimited,
            rateLimitMessage,
            setUserWrittenText,
        ],
    );

    const handleKeyDown = React.useCallback(
        (e: React.KeyboardEvent) => {
            // Handle up arrow to recall last message (only when input is empty)
            if (e.key === 'ArrowUp' && !inputText.trim() && uploadedFiles.length === 0) {
                e.preventDefault();
                recallLast();
                return;
            }

            if (e.key === 'Enter' && !e.shiftKey) {
                if (canSubmit) {
                    e.preventDefault();
                    handleSubmit();
                } else if (isProcessing) {
                    // Swallow Enter while processing so an attempted "send" doesn't
                    // insert a newline into the drafted message
                    e.preventDefault();
                }
            }
        },
        [isProcessing, canSubmit, handleSubmit, inputText, recallLast, uploadedFiles],
    );

    const isDisabled = isProcessing || effectiveDisabled;

    return (
        <div
            className={classNames(styles['chat-input'], className, {
                'p-l5-8': !noPadding,
            })}
        >
            {header && <div className={`${styles['chat-input__header']} mb-l5-16`}>{header}</div>}
            <SketchDropZone onFilesDropped={handleFilesSelected} disabled={isDisabled}>
                <form onSubmit={handleSubmit} className="position-relative">
                    <div className="position-relative bg-white">
                        {uploadedFiles.length > 0 && (
                            <ChatFileList
                                files={uploadedFiles}
                                onRemoveFile={handleRemoveFile}
                                className="chat-files-in-input"
                            />
                        )}
                        <Tooltip
                            title={effectiveTooltip}
                            show={effectiveDisabled && !!effectiveTooltip}
                        >
                            <div className={styles['chat-input__textarea-wrapper']}>
                                {atMentionReferences ? (
                                    // Attached files render as an absolutely-positioned
                                    // overlay at the top of the box, so the editor needs
                                    // the same top-padding compensation the textarea gets
                                    // via `has-files` below.
                                    <div
                                        className={classNames(
                                            styles['chat-input__mention-editor'],
                                            {
                                                [styles['chat-input__mention-editor--has-files']]:
                                                    uploadedFiles.length > 0,
                                            },
                                        )}
                                    >
                                        <MentionChatEditor
                                            ref={mentionEditorRef}
                                            value={inputText}
                                            onChange={setUserWrittenText}
                                            onSubmit={handleSubmit}
                                            onHistoryRecall={handleHistoryRecall}
                                            references={atMentionReferences}
                                            placeholder={placeholder}
                                            autoFocus={autoFocus}
                                            disabled={effectiveDisabled}
                                        />
                                    </div>
                                ) : (
                                    <TextInput
                                        ref={chatTextareaRef}
                                        text={inputText}
                                        onChange={handleInputChange}
                                        placeholder={placeholder}
                                        onKeyDown={handleKeyDown}
                                        autoFocus={autoFocus}
                                        variant={TEXT_INPUT_VARIANTS.TEXTAREA}
                                        lines={4}
                                        multiline={true}
                                        className={uploadedFiles.length > 0 ? 'has-files' : ''}
                                        disabled={effectiveDisabled}
                                        onFocus={onFocus}
                                        onBlur={onBlur}
                                    />
                                )}
                            </div>
                        </Tooltip>
                        {!hideControls && (
                            <ChatDropdown
                                onFilesSelected={handleFilesSelected}
                                disabled={isDisabled}
                                className="chat-add-dropdown"
                                canAttachFiles={canAttachFiles}
                                showSketchControls={showSketchControls}
                                hideBlueprint={hideBlueprint}
                                canUseAIVoiceover={canUseAIVoiceover}
                                onChooseExisting={canAttachFiles ? handleChooseExisting : undefined}
                                attachMenuItems={attachMenuItems}
                                attachMenuRef={attachMenuRef}
                            >
                                {recipeSlot}
                            </ChatDropdown>
                        )}
                        {isProcessing && onStop ? (
                            <PrimaryIconButton
                                icon="stop"
                                onClick={onStop}
                                variant={BUTTON_VARIANTS.FILLED}
                                color={BUTTON_COLORS.PRIMARY}
                                className={`${styles['send-button']} material-symbols-f1`}
                            />
                        ) : (
                            <PrimaryIconButton
                                icon={header ? 'arrow_forward' : 'arrow_upward'}
                                onClick={handleSubmit}
                                disabled={!canSubmit}
                                variant={BUTTON_VARIANTS.FILLED}
                                color={BUTTON_COLORS.PRIMARY}
                                className={styles['send-button']}
                            />
                        )}
                    </div>
                </form>
            </SketchDropZone>
            {Flags.chatTabDeveloperToolsEnabled() && (
                <div className="d-flex align-items-center justify-content-between mt-l5-4">
                    <div>
                        {costParts && (
                            <CostSummary parts={costParts} showWhenZero showTotalLabel={false} />
                        )}
                    </div>
                    {(() => {
                        // The developer links (agent · tools · skills) are only
                        // shown when their data is available, joined by "·".
                        const links: React.ReactNode[] = [];
                        if (agentValue && onSelectAgent) {
                            links.push(
                                <button
                                    key="agent"
                                    type="button"
                                    className="btn btn-link p-l5-0 text-decoration-none color-grey-500"
                                    style={{ fontSize: 11 }}
                                    onClick={() => setShowAgentModal(true)}
                                >
                                    {agentLabel || 'agent'}
                                </button>,
                            );
                        }
                        if (toolDefinitions?.length) {
                            links.push(
                                <button
                                    key="tools"
                                    type="button"
                                    className="btn btn-link p-l5-0 text-decoration-none color-grey-500"
                                    style={{ fontSize: 11 }}
                                    onClick={() => setShowToolsModal(true)}
                                >
                                    tools
                                </button>,
                            );
                        }
                        if (skillSurface && onSkillsChange) {
                            links.push(
                                <button
                                    key="skills"
                                    type="button"
                                    className="btn btn-link p-l5-0 text-decoration-none color-grey-500"
                                    style={{ fontSize: 11 }}
                                    onClick={() => setShowSkillsModal(true)}
                                >
                                    skills
                                </button>,
                            );
                        }
                        if (systemPrompt != null && onSystemPromptChange) {
                            links.push(
                                <button
                                    key="sys"
                                    type="button"
                                    className="btn btn-link p-l5-0 text-decoration-none color-grey-500"
                                    style={{ fontSize: 11 }}
                                    onClick={() => setShowSystemPromptModal(true)}
                                >
                                    sys
                                </button>,
                            );
                        }
                        if (modelOptions?.length && onSelectModel && selectedModel) {
                            links.push(
                                <ModelSettingsPopover
                                    key="model"
                                    selectedModel={selectedModel}
                                    modelOptions={modelOptions}
                                    onSelectModel={onSelectModel}
                                    reasoningEffort={reasoningEffort}
                                    onSelectReasoningEffort={onSelectReasoningEffort}
                                    compactionThreshold={compactionThreshold}
                                    defaultCompactionThreshold={defaultCompactionThreshold}
                                    onCompactionThresholdChange={onCompactionThresholdChange}
                                />,
                            );
                        }
                        if (conversationId) {
                            links.push(
                                <button
                                    key="registries"
                                    type="button"
                                    className="btn btn-link p-l5-0 text-decoration-none color-grey-500"
                                    style={{ fontSize: 11 }}
                                    onClick={() => setShowRegistriesModal(true)}
                                >
                                    registries
                                </button>,
                            );
                        }
                        if (conversationId) {
                            links.push(
                                <a
                                    key="conversation"
                                    href={conversationDetailUrl(conversationId)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    aria-label="Open conversation"
                                    title="Open conversation"
                                    className="btn btn-link p-l5-0 text-decoration-none color-grey-500 d-flex align-items-center"
                                >
                                    <i
                                        className="material-symbols-outlined"
                                        style={{ fontSize: 14 }}
                                        aria-hidden
                                    >
                                        open_in_new
                                    </i>
                                </a>,
                            );
                        }
                        return (
                            <div
                                className="d-flex flex-wrap align-items-center justify-content-end gap-1 color-grey-500"
                                style={{ fontSize: 11 }}
                            >
                                {links.map((link, index) => (
                                    <React.Fragment key={index}>
                                        {index > 0 && <span aria-hidden>·</span>}
                                        {link}
                                    </React.Fragment>
                                ))}
                            </div>
                        );
                    })()}
                </div>
            )}
            {footer && <div>{footer}</div>}
            <ToolsModal
                showing={showToolsModal}
                onHide={() => setShowToolsModal(false)}
                tools={toolDefinitions}
                iconForTool={iconForTool}
                childConversationSettingsForTool={childConversationSettingsForTool}
            />
            {skillSurface && onSkillsChange && (
                <SkillsModal
                    showing={showSkillsModal}
                    onHide={() => setShowSkillsModal(false)}
                    onSkillsChange={onSkillsChange}
                    surface={skillSurface}
                />
            )}
            {agentValue && onSelectAgent && (
                <AgentModal
                    showing={showAgentModal}
                    onHide={() => setShowAgentModal(false)}
                    selectedAgentValue={agentValue}
                    onSelectAgent={onSelectAgent}
                />
            )}
            <RegistriesModal
                showing={showRegistriesModal}
                onHide={() => setShowRegistriesModal(false)}
            />
            {systemPrompt != null && onSystemPromptChange && (
                <SystemPromptModal
                    showing={showSystemPromptModal}
                    onHide={() => setShowSystemPromptModal(false)}
                    systemPrompt={systemPrompt}
                    isOverridden={isSystemPromptOverridden}
                    onSave={onSystemPromptChange}
                    onReset={onSystemPromptReset}
                />
            )}
            {canAttachExistingMedia && showExistingMediaModal && (
                <UploadedMediaPickerModal
                    showing={showExistingMediaModal}
                    onMediaSelected={handleExistingMediaSelected}
                    onHidden={closeExistingMediaPicker}
                />
            )}
        </div>
    );
}
